// src/utils/carteleria/conceptosVinculacionCarteleria.js
//
// Conceptos de Cartelería que el modal "Vincular factura emitida" ofrece para un DTE (gestión, OPEX por mes de
// `meses_facturacion` y tramos HW-CAPEX), con su monto en moneda local. F3b del rediseño: los montos salen del
// motor `calendarioNegocio` y la conversión por el helper único `convertirMontoCarteleria`; resultado idéntico
// al bloque anterior del modal (oráculo en `__tests__/oraculos/conceptosVinculacionAntes.js`).
//
// El modal convierte POR FECHA del documento (`convertirAMonedaLocal(monto, moneda, pais, fecha)` de
// exchangeRateService), no con el TC vivo de `useCurrency`: ese conversor se inyecta.
import { derivarClaveHardware } from "../lineasFacturacion/resolverLinea";
import { calendarioNegocio } from "./calendarioNegocio";
import { convertirMontoCarteleria } from "./convertirMontoCarteleria";

/**
 * @param {object} c fila de `negocios_carteleria` (con los campos que pide el modal)
 * @param {{nombre: string, anoDTE: string, mesDTE: string, folio: string, pais: string,
 *   convertirAMonedaLocal: Function, hoy?: Date}} ctx
 * @returns {Array} conceptos con la forma que consume el modal
 */
export const conceptosCarteleriaDeNegocio = (
  c,
  { nombre, anoDTE, mesDTE, folio, pais, convertirAMonedaLocal, hoy = new Date() }
) => {
  const resultados = [];
  const cal = calendarioNegocio(c, { hoy });
  const defaultMoneda = pais === "Chile" ? "UF" : "PEN";
  // Misma moneda por defecto que el modal de siempre (UF en Chile / PEN en Perú cuando el negocio no la trae).
  const monedaG = c.moneda_gestion || defaultMoneda;
  const monedaC = c.moneda_contenidos || monedaG;
  const monedaH = c.moneda_hardware || defaultMoneda;
  // `convertirAMonedaLocal` ya devuelve el monto tal cual si la moneda es la local: se pasa SIEMPRE por él
  // (hacia = "LOCAL" solo evita el atajo del helper para monedas iguales, que acá no aplica).
  const aLocal = (monto, moneda, fechaRef) =>
    convertirMontoCarteleria((m, desde) => convertirAMonedaLocal(m, desde, pais, fechaRef), monto, moneda, "LOCAL");

  // meses_facturacion: cada entrada genera conceptos separados según tipo
  if (Array.isArray(c.meses_facturacion)) {
    c.meses_facturacion.forEach((mes, idx) => {
      const mesFecha = mes.facturafecha?.substring(0, 7);
      if (mesFecha !== `${anoDTE}-${mesDTE}` && mes.facturanumero !== folio) return;
      const matchR = mes.facturanumero === folio ? "folio_exacto" : "monto_similar";
      const fechaRef = mes.facturafecha || null;

      if (mes.tipo === "opex" && c.incluye_hardware && c.modalidad === "OPEX") {
        const monto = aLocal(cal.mensual.opex.monto, monedaH, fechaRef);
        resultados.push({
          tipo: "negocios_carteleria",
          subtipo: "HW-OPEX",
          id: c.id,
          ref_index: idx,
          // Clave natural de la línea (L6 tramo 2): identidad por (tipo,ano,mes,tramo), no por posición.
          linea_tipo: "opex",
          linea_ano: mes.ano,
          linea_mes: String(mes.mes).padStart(2, "0"),
          linea_tramo: 1,
          cliente: nombre,
          client_id: null,
          monto,
          facturanumero: mes.facturanumero,
          fecha: mes.facturafecha,
          plazocobro: null,
          estatus: c.estado,
          match_razon: matchR,
        });
      }

      if (mes.tipo === "gestion") {
        const gestionLocal = aLocal(cal.mensual.gestion.monto, monedaG, fechaRef);
        const contenidoLocal = aLocal(cal.mensual.contenidos.monto, monedaC, fechaRef);
        resultados.push({
          tipo: "negocios_carteleria",
          subtipo: "Gestión",
          id: c.id,
          ref_index: idx,
          linea_tipo: "gestion",
          linea_ano: mes.ano,
          linea_mes: String(mes.mes).padStart(2, "0"),
          linea_tramo: 1,
          cliente: nombre,
          client_id: null,
          monto: gestionLocal + contenidoLocal,
          facturanumero: mes.facturanumero,
          fecha: mes.facturafecha,
          plazocobro: null,
          estatus: c.estado,
          match_razon: matchR,
        });
      }
    });
  }

  // detalle_pagos_hardware: HW-CAPEX (tramos del motor; el monto cae al de `monto_hardware` si el tramo no trae)
  const detalle = Array.isArray(c.detalle_pagos_hardware) ? c.detalle_pagos_hardware : [];
  detalle.forEach((pago, idx) => {
    const pagoFecha = pago.fecha_factura?.substring(0, 7) || pago.facturafecha?.substring(0, 7);
    const pagoFolio = pago.facturanumero || "";
    if (pagoFecha !== `${anoDTE}-${mesDTE}` && pagoFolio !== folio) return;
    const claveHw = derivarClaveHardware(detalle, idx);
    const tramo = cal.hardwareCapex ? cal.hardwareCapex.tramos[idx] : null;
    resultados.push({
      // HW-CAPEX usa venta_tipo propio + índice NUMÉRICO del tramo en detalle_pagos_hardware. Namespace separado
      // de 'negocios_carteleria' (gestión/OPEX): los índices únicos de facturas_sii_vinculaciones ya discriminan
      // por venta_tipo, así que ('negocios_carteleria_hw', id, 0) no colisiona con ('negocios_carteleria', id, 0).
      tipo: "negocios_carteleria_hw",
      subtipo: "HW-CAPEX",
      id: c.id,
      ref_index: idx,
      linea_tipo: claveHw ? "hardware" : null,
      linea_ano: claveHw?.ano ?? null,
      linea_mes: claveHw?.mes ?? null,
      linea_tramo: claveHw?.tramo ?? null,
      cliente: nombre,
      client_id: null,
      monto: (tramo ? tramo.monto : pago.monto) || (cal.hardwareCapex ? cal.hardwareCapex.monto : c.monto_hardware),
      facturanumero: pagoFolio,
      fecha: pago.fecha_factura || pago.facturafecha,
      plazocobro: null,
      estatus: c.estado,
      match_razon: pagoFolio === folio ? "folio_exacto" : "monto_similar",
    });
  });
  return resultados;
};
